import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../../services/api';

// Country code to flag emoji mapping
const countryFlags: { [key: string]: string } = {
  'United States': '\uD83C\uDDFA\uD83C\uDDF8',
  'USA': '\uD83C\uDDFA\uD83C\uDDF8',
  'United Kingdom': '\uD83C\uDDEC\uD83C\uDDE7',
  'UK': '\uD83C\uDDEC\uD83C\uDDE7',
  'Canada': '\uD83C\uDDE8\uD83C\uDDE6',
  'Australia': '\uD83C\uDDE6\uD83C\uDDFA',
  'Germany': '\uD83C\uDDE9\uD83C\uDDEA',
  'France': '\uD83C\uDDEB\uD83C\uDDF7',
  'India': '\uD83C\uDDEE\uD83C\uDDF3',
  'Japan': '\uD83C\uDDEF\uD83C\uDDF5',
  'China': '\uD83C\uDDE8\uD83C\uDDF3',
  'Brazil': '\uD83C\uDDE7\uD83C\uDDF7',
  'Mexico': '\uD83C\uDDF2\uD83C\uDDFD',
  'Spain': '\uD83C\uDDEA\uD83C\uDDF8',
  'Italy': '\uD83C\uDDEE\uD83C\uDDF9',
  'Netherlands': '\uD83C\uDDF3\uD83C\uDDF1',
  'Russia': '\uD83C\uDDF7\uD83C\uDDFA',
  'South Korea': '\uD83C\uDDF0\uD83C\uDDF7',
  'Singapore': '\uD83C\uDDF8\uD83C\uDDEC',
  'UAE': '\uD83C\uDDE6\uD83C\uDDEA',
  'Saudi Arabia': '\uD83C\uDDF8\uD83C\uDDE6',
  'Indonesia': '\uD83C\uDDEE\uD83C\uDDE9',
  'Philippines': '\uD83C\uDDF5\uD83C\uDDED',
  'Pakistan': '\uD83C\uDDF5\uD83C\uDDF0',
  'Bangladesh': '\uD83C\uDDE7\uD83C\uDDE9',
  'Nigeria': '\uD83C\uDDF3\uD83C\uDDEC',
  'South Africa': '\uD83C\uDDFF\uD83C\uDDE6',
  'Egypt': '\uD83C\uDDEA\uD83C\uDDEC',
  'Turkey': '\uD83C\uDDF9\uD83C\uDDF7',
  'Poland': '\uD83C\uDDF5\uD83C\uDDF1',
  'Sweden': '\uD83C\uDDF8\uD83C\uDDEA',
  'Norway': '\uD83C\uDDF3\uD83C\uDDF4',
  'Denmark': '\uD83C\uDDE9\uD83C\uDDF0',
  'Finland': '\uD83C\uDDEB\uD83C\uDDEE',
  'Ireland': '\uD83C\uDDEE\uD83C\uDDEA',
  'New Zealand': '\uD83C\uDDF3\uD83C\uDDFF',
  'Argentina': '\uD83C\uDDE6\uD83C\uDDF7',
  'Chile': '\uD83C\uDDE8\uD83C\uDDF1',
  'Colombia': '\uD83C\uDDE8\uD83C\uDDF4',
  'Malaysia': '\uD83C\uDDF2\uD83C\uDDFE',
  'Thailand': '\uD83C\uDDF9\uD83C\uDDED',
  'Vietnam': '\uD83C\uDDFB\uD83C\uDDF3',
  'Unknown': '\uD83C\uDF10'
};

const formatCompact = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds === 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const extractPath = (url: string): string => {
  try { return new URL(url).pathname || '/'; } catch { return url || '/'; }
};

const getFlag = (country: string): string => countryFlags[country] || '\uD83C\uDF10';

const getDeviceIcon = (device: string): string => {
  const d = device?.toLowerCase() || '';
  if (d.includes('mobile') || d.includes('phone')) return 'bi-phone';
  if (d.includes('tablet') || d.includes('ipad')) return 'bi-tablet';
  return 'bi-laptop';
};

const getDeviceColor = (device: string): string => {
  const d = device?.toLowerCase() || '';
  if (d.includes('mobile') || d.includes('phone')) return '#10B981';
  if (d.includes('tablet') || d.includes('ipad')) return '#F59E0B';
  return '#3B82F6';
};

interface LiveVisitor {
  id: number;
  visitorId: string;
  country: string;
  city?: string;
  device: string;
  browser: string;
  os?: string;
  currentPage?: string;
  sessionDuration: number;
  pageViews: number;
  lastActivity: string;
  joinedAt: string;
}

interface LiveData {
  onlineCount: number;
  visitors: LiveVisitor[];
  deviceBreakdown: Array<{ device: string; count: number }>;
  countryBreakdown: Array<{ country: string; count: number }>;
  recentVisitors: Array<{
    visitorId: string;
    country: string;
    device: string;
    browser: string;
    joinedAt: string;
  }>;
  lastUpdated: string;
}

interface FloatingVisitor {
  id: string;  // visitorId string
  country: string;
  device: string;
  currentPage?: string;
  x: number;
  y: number;
  angle: number;
  isNew: boolean;
}

interface Props {
  assetId: number;
  assetName?: string;
}

const LiveVisitorsHub: React.FC<Props> = ({ assetId, assetName = 'Website' }) => {
  const navigate = useNavigate();
  const [liveData, setLiveData] = useState<LiveData | null>(null);
  const [floatingVisitors, setFloatingVisitors] = useState<FloatingVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousVisitorsRef = useRef<Set<string>>(new Set());

  const fetchLiveData = async () => {
    try {
      const response = await analyticsAPI.getLiveVisitors(assetId);
      const data = response.data.data as LiveData;
      setLiveData(data);
      setError(null);

      const currentVisitorIds = new Set(data.visitors.map((v: LiveVisitor) => v.visitorId));
      const newVisitors = data.visitors.filter(
        (v: LiveVisitor) => !previousVisitorsRef.current.has(v.visitorId)
      );

      const newFloating: FloatingVisitor[] = data.visitors.slice(0, 12).map((v: LiveVisitor, index: number) => {
        const angle = (index / Math.min(data.visitors.length, 12)) * 2 * Math.PI - Math.PI / 2;
        const radius = 110;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        return {
          id: v.visitorId,
          country: v.country,
          device: v.device,
          currentPage: v.currentPage,
          x,
          y,
          angle: angle * (180 / Math.PI),
          isNew: newVisitors.some((nv: LiveVisitor) => nv.visitorId === v.visitorId)
        };
      });

      setFloatingVisitors(newFloating);
      previousVisitorsRef.current = currentVisitorIds;
    } catch (err: any) {
      console.error('Failed to fetch live visitors:', err);
      setError('Failed to load live data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 10000);
    return () => clearInterval(interval);
  }, [assetId]);

  const goToVisitorLog = (visitorId?: string) => {
    const url = visitorId
      ? `/analytics/visitor-log?visitor=${encodeURIComponent(visitorId)}`
      : '/analytics/visitor-log';
    navigate(url);
  };

  if (loading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3 mb-0">Loading live data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <i className="bi bi-exclamation-circle text-danger fs-1 d-block mb-3"></i>
          <p className="text-muted mb-0">{error}</p>
        </div>
      </div>
    );
  }

  const onlineCount = liveData?.onlineCount || 0;

  return (
    <div className="card border-0 shadow-sm overflow-hidden">
      <div className="card-header bg-white border-0 py-3">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-broadcast me-2"></i>
            Online Now
          </h5>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => goToVisitorLog()}
            >
              <i className="bi bi-people me-1"></i>Visitor Log
            </button>
            <span className="live-indicator me-2"></span>
            <span className="badge bg-danger live-badge">LIVE</span>
          </div>
        </div>
      </div>

      <div className="card-body p-0">
        {/* Hub Visualization */}
        <div
          ref={containerRef}
          className="live-hub-container position-relative"
          style={{ height: '300px', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
        >
          <svg className="position-absolute w-100 h-100" style={{ top: 0, left: 0, pointerEvents: 'none' }}>
            {floatingVisitors.map((visitor, index) => (
              <line
                key={`line-${visitor.id}`}
                x1="50%"
                y1="50%"
                x2={`calc(50% + ${visitor.x}px)`}
                y2={`calc(50% + ${visitor.y}px)`}
                stroke={getDeviceColor(visitor.device)}
                strokeWidth="2"
                strokeDasharray="4,4"
                opacity="0.4"
                className="connection-line"
                style={{ animationDelay: `${index * 0.1}s` }}
              />
            ))}
          </svg>

          {/* Central Hub */}
          <div
            className="central-hub position-absolute"
            style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}
          >
            <div className="hub-outer-ring"></div>
            <div className="hub-inner-ring"></div>
            <div className="hub-core d-flex flex-column align-items-center justify-content-center">
              <span className="online-count">{formatCompact(onlineCount)}</span>
              <span className="online-label">online</span>
            </div>
          </div>

          {/* Floating Visitors — clickable */}
          {floatingVisitors.map((visitor, index) => (
            <div
              key={visitor.id}
              className={`floating-visitor ${visitor.isNew ? 'new-visitor' : ''}`}
              style={{
                position: 'absolute',
                top: `calc(50% + ${visitor.y}px)`,
                left: `calc(50% + ${visitor.x}px)`,
                transform: 'translate(-50%, -50%)',
                animationDelay: `${index * 0.15}s`,
                zIndex: 5,
                cursor: 'pointer',
              }}
              title={`${visitor.country}${visitor.currentPage ? ' · ' + extractPath(visitor.currentPage) : ''} — Click to view journey`}
              onClick={() => goToVisitorLog(visitor.id)}
            >
              <div className="visitor-bubble d-flex flex-column align-items-center position-relative">
                {/* Green live dot on bubble */}
                <span className="bubble-live-dot"></span>
                <div
                  className="device-icon-wrapper mb-1"
                  style={{ backgroundColor: getDeviceColor(visitor.device) }}
                >
                  <i className={`bi ${getDeviceIcon(visitor.device)} text-white`}></i>
                </div>
                <div className="visitor-info text-center">
                  <span className="country-flag">{getFlag(visitor.country)}</span>
                  <small className="d-block text-muted country-name">
                    {visitor.country.length > 8 ? visitor.country.substring(0, 8) + '...' : visitor.country}
                  </small>
                </div>
              </div>
            </div>
          ))}

          {onlineCount === 0 && (
            <div className="no-visitors-message position-absolute" style={{ top: '75%', left: '50%', transform: 'translateX(-50%)' }}>
              <p className="text-muted mb-0 small">Waiting for visitors...</p>
            </div>
          )}
        </div>

        {/* Stats Bar */}
        <div className="stats-bar p-3 bg-white border-top">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <h6 className="text-muted small mb-2">
                <i className="bi bi-device-hdd me-1"></i>Devices Online
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {liveData?.deviceBreakdown && liveData.deviceBreakdown.length > 0 ? (
                  liveData.deviceBreakdown.map((device, index) => (
                    <span
                      key={index}
                      className="badge d-flex align-items-center"
                      style={{ backgroundColor: getDeviceColor(device.device), color: 'white' }}
                    >
                      <i className={`bi ${getDeviceIcon(device.device)} me-1`}></i>
                      {device.device}: {formatCompact(device.count)}
                    </span>
                  ))
                ) : (
                  <span className="text-muted small">No devices online</span>
                )}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <h6 className="text-muted small mb-2">
                <i className="bi bi-globe me-1"></i>Top Countries
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {liveData?.countryBreakdown && liveData.countryBreakdown.length > 0 ? (
                  liveData.countryBreakdown.slice(0, 5).map((country, index) => (
                    <span key={index} className="badge bg-light text-dark">
                      {getFlag(country.country)} {country.country}: {formatCompact(country.count)}
                    </span>
                  ))
                ) : (
                  <span className="text-muted small">No country data</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live Visitors List */}
        {liveData?.visitors && liveData.visitors.length > 0 && (
          <div className="border-top">
            <div className="px-3 pt-3 pb-2 d-flex justify-content-between align-items-center">
              <h6 className="text-muted small mb-0 fw-semibold d-flex align-items-center gap-2">
                <span className="live-dot-green"></span>
                Live Now — {onlineCount} {onlineCount === 1 ? 'visitor' : 'visitors'}
              </h6>
              <button
                className="btn btn-link btn-sm text-primary p-0 text-decoration-none"
                style={{ fontSize: '12px' }}
                onClick={() => goToVisitorLog()}
              >
                View all in Visitor Log →
              </button>
            </div>
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              {liveData.visitors.slice(0, 15).map((visitor) => (
                <div
                  key={visitor.visitorId}
                  className="live-visitor-row d-flex align-items-center px-3 py-2"
                  onClick={() => goToVisitorLog(visitor.visitorId)}
                  title="Click to view visitor journey"
                >
                  <span className="live-dot-small me-2 flex-shrink-0"></span>
                  <span className="me-2 flex-shrink-0" style={{ fontSize: '16px' }}>{getFlag(visitor.country)}</span>
                  <i
                    className={`bi ${getDeviceIcon(visitor.device)} me-2 flex-shrink-0`}
                    style={{ color: getDeviceColor(visitor.device), fontSize: '13px' }}
                  ></i>
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-center gap-1">
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          fontFamily: 'monospace',
                          color: '#374151',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '180px',
                          display: 'block',
                        }}
                        title={visitor.currentPage}
                      >
                        {visitor.currentPage ? extractPath(visitor.currentPage) : '/'}
                      </span>
                    </div>
                    <small className="text-muted" style={{ fontSize: '11px' }}>
                      {visitor.country}{visitor.city ? `, ${visitor.city}` : ''}
                      {visitor.browser ? ` · ${visitor.browser}` : ''}
                    </small>
                  </div>
                  <div className="text-end flex-shrink-0 ms-2">
                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>
                      {formatDuration(visitor.sessionDuration)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                      {visitor.pageViews}p
                    </div>
                  </div>
                  <i className="bi bi-chevron-right ms-2 text-muted" style={{ fontSize: '10px', flexShrink: 0 }}></i>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .live-indicator {
          width: 10px;
          height: 10px;
          background-color: #ef4444;
          border-radius: 50%;
          animation: pulse-live 1.5s ease-in-out infinite;
        }

        .live-badge {
          animation: badge-pulse 2s ease-in-out infinite;
        }

        @keyframes badge-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes pulse-live {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }

        .live-dot-green {
          display: inline-block;
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          flex-shrink: 0;
          animation: green-pulse 1.5s ease-in-out infinite;
        }

        .live-dot-small {
          display: inline-block;
          width: 7px;
          height: 7px;
          background: #10b981;
          border-radius: 50%;
          animation: green-pulse 1.5s ease-in-out infinite;
        }

        @keyframes green-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
          50% { opacity: 0.85; box-shadow: 0 0 0 4px rgba(16, 185, 129, 0); }
        }

        .bubble-live-dot {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          border: 1.5px solid white;
          animation: green-pulse 1.5s ease-in-out infinite;
          z-index: 2;
        }

        .central-hub {
          width: 90px;
          height: 90px;
        }

        .hub-outer-ring {
          position: absolute;
          top: -12px; left: -12px; right: -12px; bottom: -12px;
          border: 2px solid rgba(59, 130, 246, 0.2);
          border-radius: 50%;
          animation: ring-pulse 2s ease-in-out infinite;
        }

        .hub-inner-ring {
          position: absolute;
          top: -6px; left: -6px; right: -6px; bottom: -6px;
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 50%;
          animation: ring-pulse 2s ease-in-out infinite 0.5s;
        }

        .hub-core {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 50%;
          box-shadow: 0 4px 20px rgba(59, 130, 246, 0.4);
        }

        .online-count {
          font-size: 28px;
          font-weight: 700;
          color: white;
          line-height: 1;
        }

        .online-label {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        @keyframes ring-pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.1); opacity: 0.3; }
        }

        .floating-visitor {
          animation: float-in 0.6s ease-out forwards;
          opacity: 0;
        }

        .new-visitor {
          animation: float-in-new 0.6s ease-out forwards, glow-pulse 2s ease-in-out 3;
        }

        @keyframes float-in {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }

        @keyframes float-in-new {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0) rotate(-180deg); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
        }

        @keyframes glow-pulse {
          0%, 100% { filter: drop-shadow(0 0 5px rgba(16, 185, 129, 0.5)); }
          50% { filter: drop-shadow(0 0 15px rgba(16, 185, 129, 0.8)); }
        }

        .visitor-bubble {
          transition: transform 0.2s ease;
        }

        .floating-visitor:hover .visitor-bubble {
          transform: scale(1.2);
        }

        .device-icon-wrapper {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          font-size: 14px;
        }

        .country-flag { font-size: 14px; }

        .country-name {
          font-size: 8px;
          max-width: 55px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .connection-line {
          animation: dash-flow 2s linear infinite;
        }

        @keyframes dash-flow {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -16; }
        }

        .live-visitor-row {
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          transition: background-color 0.15s ease;
        }

        .live-visitor-row:hover {
          background-color: #f0fdf4;
        }

        .live-visitor-row:last-child {
          border-bottom: none;
        }
      `}</style>
    </div>
  );
};

export default LiveVisitorsHub;
